import { SESSION_TTL_MS } from '../config';
import { generateShortSessionId } from './UrlSessionSync';

export interface ISessionData {
  sessionId: string;
  lastActivity: number;
  createdAt: number;
  visitCount: number;
  firstVisit: number;
}

/**
 * Gerenciador de sessão persistente com suporte a cookies e localStorage
 */
export class GetSession {
  private sessionData: ISessionData;
  private storageKey: string = 'gomarke_session';
  private cookieKey: string = 'gomarke_session_id';
  private sessionTimeout: number = SESSION_TTL_MS;
  private cookieExpirationDays: number = 365; // 1 ano para cookies
  private debug: boolean = false;

  constructor(sessionId?: string, debug: boolean = false) {
    this.debug = debug;
    this.sessionData = this.initializeSession(sessionId);
    this.startActivityTracking();
  }

  /**
   * Inicializa ou recupera dados da sessão
   */
  private initializeSession(providedSessionId?: string): ISessionData {
    const now = Date.now();

    // Tenta recuperar sessão do localStorage
    const stored = this.getStoredSession();

    // Tenta recuperar sessionId do cookie
    const cookieSessionId = this.getCookie(this.cookieKey);

    if (stored && this.isSessionValid(stored)) {
      // Sessão existente válida - atualiza última atividade
      stored.lastActivity = now;
      this.saveSession(stored);

      // Garante que o cookie também está atualizado
      if (cookieSessionId !== stored.sessionId) {
        this.setCookie(this.cookieKey, stored.sessionId);
      }

      return stored;
    }

    // Determina o sessionId a usar
    let finalSessionId: string;

    if (providedSessionId) {
      finalSessionId = providedSessionId;
    } else if (cookieSessionId) {
      finalSessionId = cookieSessionId;
    } else {
      finalSessionId = this.generateSessionId();
    }

    // Nova sessão ou sessão expirada
    const isFirstVisit = !stored && !cookieSessionId;
    const newSession: ISessionData = {
      sessionId: finalSessionId,
      lastActivity: now,
      createdAt: stored?.createdAt || now,
      visitCount: stored ? stored.visitCount + 1 : 1,
      firstVisit: stored?.firstVisit || now,
    };

    this.saveSession(newSession);
    this.setCookie(this.cookieKey, finalSessionId);

    if (this.debug) {
      console.log('[GetSession] Nova sessão criada:', {
        sessionId: finalSessionId,
        isFirstVisit,
        visitCount: newSession.visitCount,
      });
    }

    return newSession;
  }

  /**
   * Gera um novo sessionId único
   */
  private generateSessionId(): string {
    return generateShortSessionId();
  }

  /**
   * Verifica se a sessão ainda é válida
   */
  private isSessionValid(session: ISessionData): boolean {
    const now = Date.now();
    return now - session.lastActivity < this.sessionTimeout;
  }

  /**
   * Salva dados da sessão no localStorage
   */
  private saveSession(session: ISessionData): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(this.storageKey, JSON.stringify(session));
      }
    } catch (error) {
      if (this.debug) {
        console.warn(
          '[GetSession] Erro ao salvar sessão no localStorage:',
          error
        );
      }
    }
  }

  /**
   * Recupera dados da sessão do localStorage
   */
  private getStoredSession(): ISessionData | null {
    try {
      if (typeof localStorage === 'undefined') {
        return null;
      }
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) return null;

      const parsed = JSON.parse(stored) as ISessionData;

      // Valida estrutura básica
      if (!parsed.sessionId || !parsed.lastActivity) {
        return null;
      }

      return parsed;
    } catch (error) {
      if (this.debug) {
        console.warn(
          '[GetSession] Erro ao recuperar sessão do localStorage:',
          error
        );
      }
      return null;
    }
  }

  /**
   * Define um cookie
   */
  private setCookie(name: string, value: string): void {
    try {
      if (typeof document === 'undefined') return;

      const expirationDate = new Date();
      expirationDate.setTime(
        expirationDate.getTime() +
          this.cookieExpirationDays * 24 * 60 * 60 * 1000
      );

      const expires = `expires=${expirationDate.toUTCString()}`;
      document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
    } catch (error) {
      if (this.debug) {
        console.warn('[GetSession] Erro ao definir cookie:', error);
      }
    }
  }

  /**
   * Obtém um cookie
   */
  private getCookie(name: string): string | null {
    try {
      if (typeof document === 'undefined') return null;

      const nameEQ = `${name}=`;
      const ca = document.cookie.split(';');

      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) {
          return c.substring(nameEQ.length, c.length);
        }
      }
      return null;
    } catch (error) {
      if (this.debug) {
        console.warn('[GetSession] Erro ao ler cookie:', error);
      }
      return null;
    }
  }

  /**
   * Remove um cookie
   */
  private removeCookie(name: string): void {
    try {
      if (typeof document === 'undefined') return;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    } catch (error) {
      if (this.debug) {
        console.warn('[GetSession] Erro ao remover cookie:', error);
      }
    }
  }

  /**
   * Inicia o rastreamento de atividade para renovar sessão automaticamente
   */
  private startActivityTracking(): void {
    if (typeof window === 'undefined') return;

    // Atualiza atividade em eventos do usuário
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];
    const updateActivity = () => {
      this.updateLastActivity();
    };

    // Throttle para não atualizar muito frequentemente
    let lastUpdate = 0;
    const throttledUpdate = () => {
      const now = Date.now();
      if (now - lastUpdate > 60000) {
        // Atualiza no máximo a cada 1 minuto
        lastUpdate = now;
        updateActivity();
      }
    };

    events.forEach(event => {
      window.addEventListener(event, throttledUpdate, { passive: true });
    });

    // Atualiza ao focar na janela
    window.addEventListener('focus', updateActivity, { passive: true });
  }

  /**
   * Atualiza a última atividade da sessão
   */
  public updateLastActivity(): void {
    const now = Date.now();
    this.sessionData.lastActivity = now;
    this.saveSession(this.sessionData);
  }

  /**
   * Renova a sessão (cria nova se expirada)
   */
  public renewSession(): void {
    if (!this.isSessionValid(this.sessionData)) {
      const newSessionId = this.generateSessionId();
      this.sessionData = this.initializeSession(newSessionId);
    } else {
      this.updateLastActivity();
    }
  }

  /**
   * Obtém dados da sessão
   */
  public getSessionData(): ISessionData {
    // Atualiza última atividade antes de retornar
    this.updateLastActivity();
    return { ...this.sessionData };
  }

  /**
   * Obtém apenas o sessionId
   */
  public getSessionId(): string {
    return this.sessionData.sessionId;
  }

  /**
   * Verifica se a sessão está ativa
   */
  public isActive(): boolean {
    return this.isSessionValid(this.sessionData);
  }

  /**
   * Limpa dados da sessão
   */
  public clearSession(): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(this.storageKey);
      }
      this.removeCookie(this.cookieKey);

      if (this.debug) {
        console.log('[GetSession] Sessão limpa');
      }
    } catch (error) {
      if (this.debug) {
        console.warn('[GetSession] Erro ao limpar sessão:', error);
      }
    }
  }

  /**
   * Exporta dados da sessão
   */
  public exportSessionData(): string {
    return JSON.stringify(
      {
        session: this.sessionData,
        isActive: this.isActive(),
        timeUntilExpiration:
          this.sessionTimeout - (Date.now() - this.sessionData.lastActivity),
      },
      null,
      2
    );
  }
}
